import { useEffect, useMemo, useState } from "react";
import { CustomTable } from "../../../componentes/table/CustomTable";
import DateMasked from "../../../componentes/inputMask/DateMasked";
import Modal from "../../../componentes/modal/modal";
import Input from "../../../componentes/input/input";
import DropDown from "../../../componentes/dropdown/dropdown";
import { Dialog } from "primereact/dialog";
import { toast } from "react-toastify";
import RomaneioService from "../../../services/resource/romaneioService";
import FuncionarioService from "../../../services/resource/funcionarioService";
import { ANOS, MESES } from "../../../utils/options";
import { formatLocalDate } from "../../../utils/formatDate";

const formatDate = (value) => value ? formatLocalDate(value) : "—";

const FILIAL_ID = 4; // York Caxias — único filial que opera com romaneios/pedidos DA

export default function RomaneioPage() {
    const service = useMemo(() => new RomaneioService(), []);
    const funcionarioService = useMemo(() => new FuncionarioService(), []);

    const now = new Date();
    let anoInicial = now.getFullYear();
    let mesInicial = now.getMonth() + 1;
    if (now.getDate() >= 27) {
        if (mesInicial === 12) { mesInicial = 1; anoInicial += 1; }
        else { mesInicial += 1; }
    }

    const [lista, setLista] = useState([]);
    const [motoristas, setMotoristas] = useState([]);
    const [auxiliares, setAuxiliares] = useState([]);
    const [loading, setLoading] = useState(false);
    const [registro, setRegistro] = useState(null);
    const [filtro, setFiltro] = useState({
        ano: anoInicial,
        mes: mesInicial,
    });

    const [pedidosRomaneio, setPedidosRomaneio] = useState(null);
    const [pedidos, setPedidos] = useState([]);
    const [loadingPedidos, setLoadingPedidos] = useState(false);

    const [itensPedido, setItensPedido] = useState(null);
    const [itens, setItens] = useState([]);
    const [loadingItens, setLoadingItens] = useState(false);
    const [itemSelecionado, setItemSelecionado] = useState(null);

    useEffect(() => {
        funcionarioService.listarMotoristas(FILIAL_ID).then(res => setMotoristas(res.data ?? []));
        funcionarioService.listarAuxiliaresDeCarga(FILIAL_ID).then(res => setAuxiliares(res.data ?? []));
    }, []);

    async function buscar(e) {
        e.preventDefault();
        await carregarRomaneios();
    }

    async function carregarRomaneios() {
        setLoading(true);
        try {
            const response = await service.listar(null, filtro.ano, filtro.mes, FILIAL_ID);
            setLista(response.data ?? []);
        } finally {
            setLoading(false);
        }
    }

    function selecionar(item) {
        setRegistro({
            ...item,
            auxiliar1Id: item.auxiliares?.[0]?.id ?? null,
            auxiliar2Id: item.auxiliares?.[1]?.id ?? null,
        });
    }

    function alterarCampo(campo, valor) {
        setRegistro((atual) => ({ ...atual, [campo]: valor }));
    }

    async function salvar() {
        if (!registro) return;

        setLoading(true);
        try {
            const auxiliaresIds = [registro.auxiliar1Id, registro.auxiliar2Id].filter(Boolean);

            const dto = {
                motoristaId: registro.motoristaId || null,
                auxiliaresIds,
                dataEmissao: registro.dataEmissao || null,
                dataAcerto: registro.dataAcerto || null,
            };

            await service.atualizarRomaneio(registro.id, dto);
            setRegistro(null);
            await carregarRomaneios();
        } catch (e) {
            toast.error(e?.response?.data?.message || "Erro ao atualizar romaneio");
        } finally {
            setLoading(false);
        }
    }

    async function abrirPedidos(item) {
        setPedidosRomaneio(item);
        setLoadingPedidos(true);
        try {
            const res = await service.listarPedidos(item.id);
            setPedidos(res.data ?? []);
        } finally {
            setLoadingPedidos(false);
        }
    }

    function fecharPedidos() {
        setPedidosRomaneio(null);
        setPedidos([]);
    }

    async function abrirItens(pedido) {
        setItensPedido(pedido);
        setLoadingItens(true);
        try {
            const res = await service.listarItensPedido(pedido.id);
            setItens(res.data ?? []);
        } finally {
            setLoadingItens(false);
        }
    }

    function fecharItens() {
        setItensPedido(null);
        setItens([]);
    }

    function selecionarItem(item) {
        setItemSelecionado({ ...item });
    }

    function alterarCampoItem(campo, valor) {
        setItemSelecionado((atual) => ({ ...atual, [campo]: valor }));
    }

    async function salvarItem() {
        if (!itemSelecionado) return;

        setLoadingItens(true);
        try {
            const dto = {
                caixas: itemSelecionado.caixas === "" ? null : Number(itemSelecionado.caixas),
                caixasVenda: itemSelecionado.caixasVenda === "" ? null : Number(itemSelecionado.caixasVenda),
                caixasDevolucao: itemSelecionado.caixasDevolucao === "" ? null : Number(itemSelecionado.caixasDevolucao),
                cubos: itemSelecionado.cubos === "" ? null : Number(itemSelecionado.cubos),
                caixasUnitarias: itemSelecionado.caixasUnitarias === "" ? null : Number(itemSelecionado.caixasUnitarias),
                caixasUnitariasDevolvidas: itemSelecionado.caixasUnitariasDevolvidas === "" ? null : Number(itemSelecionado.caixasUnitariasDevolvidas),
            };

            await service.atualizarItemPedido(itemSelecionado.id, dto);
            toast.success("Item atualizado com sucesso!");
            setItemSelecionado(null);

            const res = await service.listarItensPedido(itensPedido.id);
            setItens(res.data ?? []);
        } catch (e) {
            toast.error(e?.response?.data?.message || "Erro ao atualizar item do pedido");
        } finally {
            setLoadingItens(false);
        }
    }

    const columns = useMemo(() => [
        { key: "id", label: "Romaneio", sortable: true, stickyLeft: true, className: "font-medium" },
        { key: "dataEmissao", label: "Data Emissão", sortable: true, render: formatDate },
        { key: "dataAcerto", label: "Data Acerto", sortable: true, render: formatDate },
        { key: "motoristaNome", label: "Motorista", sortable: true },
        { key: "auxiliar1Nome", label: "Auxiliar 1", sortable: true },
        { key: "auxiliar2Nome", label: "Auxiliar 2", sortable: true },
        { key: "quantidadePedidos", label: "Pedidos", sortable: true, center: true },
        { key: "quantidadeEntregas", label: "Entregas", sortable: true, center: true },
        { key: "prazo", label: "Prazo", sortable: true },
        {
            key: "acoes",
            label: "Ações",
            sortable: false,
            filterable: false,
            noSelected: true,
            render: (_, item) => (
                <div className="flex gap-1.5">
                    <button
                        type="button"
                        onClick={() => selecionar(item)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-primary/20 bg-primary/5 px-2.5 py-1.5 text-[10px] font-semibold text-primary transition-colors duration-150 hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary/20"
                    >
                        <i className="fa fa-pencil" /> Editar
                    </button>

                    <button
                        type="button"
                        onClick={() => abrirPedidos(item)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 text-[10px] font-semibold text-foreground transition-colors duration-150 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary/20"
                    >
                        <i className="fa fa-list" /> Ver Pedidos
                    </button>
                </div>
            ),
        }
    ], []);

    const colunasPedidos = useMemo(() => [
        { key: "id", label: "Pedido", stickyLeft: true, className: "font-medium" },
        { key: "notaFiscal", label: "Nota Fiscal" },
        { key: "clienteCodigo", label: "Cód. Cliente" },
        { key: "clienteRazaoSocial", label: "Cliente" },
        { key: "clienteFantasia", label: "Fantasia" },
        { key: "valorPedido", label: "Valor", render: v => v != null ? Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "—" },
        {
            key: "acoes",
            label: "Ações",
            sortable: false,
            filterable: false,
            noSelected: true,
            render: (_, item) => (
                <button
                    type="button"
                    onClick={() => abrirItens(item)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-primary/20 bg-primary/5 px-2.5 py-1.5 text-[10px] font-semibold text-primary transition-colors duration-150 hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                    <i className="fa fa-boxes-stacked" /> Ver Itens
                </button>
            ),
        }
    ], []);

    const colunasItens = useMemo(() => [
        { key: "produtoCodigo", label: "Cód. Produto", stickyLeft: true },
        { key: "produtoNome", label: "Produto" },
        { key: "embalagem", label: "Embalagem" },
        { key: "caixas", label: "Caixas", center: true },
        { key: "caixasVenda", label: "Caixas Venda", center: true },
        { key: "caixasDevolucao", label: "Caixas Devolução", center: true },
        { key: "cubos", label: "Cubos", center: true },
        { key: "caixasUnitarias", label: "Caixas Unitárias", center: true },
        { key: "caixasUnitariasDevolvidas", label: "Cx. Unit. Devolvidas", center: true },
        {
            key: "acoes",
            label: "Ações",
            sortable: false,
            filterable: false,
            noSelected: true,
            render: (_, item) => (
                <button
                    type="button"
                    onClick={() => selecionarItem(item)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-primary/20 bg-primary/5 px-2.5 py-1.5 text-[10px] font-semibold text-primary transition-colors duration-150 hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                    <i className="fa fa-pencil" /> Editar
                </button>
            ),
        }
    ], []);

    return (
        <div>
            <main className="space-y-4 pt-3">
                <section>
                    <form onSubmit={buscar} className="grid grid-cols-1 gap-3 mb-3 sm:grid-cols-2 xl:grid-cols-[minmax(160px,1fr)_minmax(160px,1fr)_auto] xl:items-end">
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground/80">Mês</label>
                            <select
                                value={filtro.mes}
                                onChange={e => setFiltro((atual) => ({ ...atual, mes: Number(e.target.value) }))}
                                className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground outline-none"
                            >
                                {MESES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground/80">Ano</label>
                            <select
                                value={filtro.ano}
                                onChange={e => setFiltro((atual) => ({ ...atual, ano: Number(e.target.value) }))}
                                className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground outline-none"
                            >
                                {ANOS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                            </select>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-xs transition-all duration-150 hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            <i className={loading ? "fa fa-spinner fa-spin" : "fa fa-search"} /> Buscar
                        </button>
                    </form>

                    <CustomTable
                        data={lista}
                        columns={columns}
                        loading={loading}
                        pagination
                        pageSize={20}
                        exportable
                        showHeader={false}
                        exportFilename="Romaneios"
                        className="text-[11px]"
                    />
                </section>

                <Modal
                    isOpen={Boolean(registro)}
                    onClose={() => setRegistro(null)}
                    title={`Editar Romaneio ${registro?.id ?? ""}`}
                    subtitle="Altere os campos necessários e salve as atualizações."
                    className="w-[92%] md:w-[700px]"
                    footer={
                        <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
                            <button
                                type="button"
                                onClick={() => setRegistro(null)}
                                className="rounded-lg px-4 py-2 text-sm font-semibold text-muted-foreground hover:bg-muted transition-colors duration-150"
                            >
                                Cancelar
                            </button>

                            <button
                                type="button"
                                onClick={salvar}
                                disabled={loading}
                                className="inline-flex items-center gap-2 rounded-lg bg-primary hover:brightness-95 px-4 py-2 text-sm font-semibold text-primary-foreground transition-all duration-150"
                            >
                                <i className={loading ? "fa fa-spinner fa-spin" : "fa fa-save"} />
                                Salvar alterações
                            </button>
                        </div>
                    }
                >
                    {registro && (
                        <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
                            <DropDown
                                label="Motorista"
                                options={motoristas}
                                optionLabel="nome"
                                optionValue="id"
                                showClear
                                value={registro.motoristaId ?? null}
                                onChange={(e) => alterarCampo("motoristaId", e.value)}
                            />

                            <DropDown
                                label="Auxiliar 1"
                                options={auxiliares}
                                optionLabel="nome"
                                optionValue="id"
                                showClear
                                value={registro.auxiliar1Id ?? null}
                                onChange={(e) => alterarCampo("auxiliar1Id", e.value)}
                            />

                            <DropDown
                                label="Auxiliar 2"
                                options={auxiliares}
                                optionLabel="nome"
                                optionValue="id"
                                showClear
                                value={registro.auxiliar2Id ?? null}
                                onChange={(e) => alterarCampo("auxiliar2Id", e.value)}
                            />

                            <DateMasked
                                label="Data Emissão"
                                value={registro.dataEmissao ?? ""}
                                onChange={(v) => alterarCampo("dataEmissao", v)}
                            />

                            <DateMasked
                                label="Data Acerto"
                                value={registro.dataAcerto ?? ""}
                                onChange={(v) => alterarCampo("dataAcerto", v)}
                            />
                        </div>
                    )}
                </Modal>

                <Dialog
                    header={`Pedidos do Romaneio ${pedidosRomaneio?.id ?? ""}`}
                    visible={Boolean(pedidosRomaneio)}
                    onHide={fecharPedidos}
                    className="w-[95%]"
                    contentClassName="p-0"
                    blockScroll
                >
                    <div className="p-3">
                        <CustomTable
                            data={pedidos}
                            columns={colunasPedidos}
                            loading={loadingPedidos}
                            className="text-[11px]"
                        />
                    </div>
                </Dialog>

                <Dialog
                    header={`Itens do Pedido ${itensPedido?.id ?? ""}`}
                    visible={Boolean(itensPedido)}
                    onHide={fecharItens}
                    className="w-[95%]"
                    contentClassName="p-0"
                    blockScroll
                >
                    <div className="p-3">
                        <CustomTable
                            data={itens}
                            columns={colunasItens}
                            loading={loadingItens}
                            className="text-[11px]"
                        />
                    </div>
                </Dialog>

                <Modal
                    isOpen={Boolean(itemSelecionado)}
                    onClose={() => setItemSelecionado(null)}
                    title={`Editar Item — ${itemSelecionado?.produtoNome ?? ""}`}
                    subtitle="Ajuste os valores de caixas e cubos do item do pedido."
                    className="w-[92%] md:w-[480px]"
                    footer={
                        <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
                            <button
                                type="button"
                                onClick={() => setItemSelecionado(null)}
                                className="rounded-lg px-4 py-2 text-sm font-semibold text-muted-foreground hover:bg-muted transition-colors duration-150"
                            >
                                Cancelar
                            </button>

                            <button
                                type="button"
                                onClick={salvarItem}
                                disabled={loadingItens}
                                className="inline-flex items-center gap-2 rounded-lg bg-primary hover:brightness-95 px-4 py-2 text-sm font-semibold text-primary-foreground transition-all duration-150"
                            >
                                <i className={loadingItens ? "fa fa-spinner fa-spin" : "fa fa-save"} />
                                Salvar
                            </button>
                        </div>
                    }
                >
                    {itemSelecionado && (
                        <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
                            <Input
                                label="Caixas"
                                type="number"
                                value={itemSelecionado.caixas ?? ""}
                                onChange={(e) => alterarCampoItem("caixas", e.target.value)}
                            />

                            <Input
                                label="Caixas Venda"
                                type="number"
                                value={itemSelecionado.caixasVenda ?? ""}
                                onChange={(e) => alterarCampoItem("caixasVenda", e.target.value)}
                            />

                            <Input
                                label="Caixas Devolução"
                                type="number"
                                value={itemSelecionado.caixasDevolucao ?? ""}
                                onChange={(e) => alterarCampoItem("caixasDevolucao", e.target.value)}
                            />

                            <Input
                                label="Cubos"
                                type="number"
                                step="0.00001"
                                value={itemSelecionado.cubos ?? ""}
                                onChange={(e) => alterarCampoItem("cubos", e.target.value)}
                            />

                            <Input
                                label="Caixas Unitárias"
                                type="number"
                                step="0.00001"
                                value={itemSelecionado.caixasUnitarias ?? ""}
                                onChange={(e) => alterarCampoItem("caixasUnitarias", e.target.value)}
                            />

                            <Input
                                label="Caixas Unitárias Devolvidas"
                                type="number"
                                step="0.00001"
                                value={itemSelecionado.caixasUnitariasDevolvidas ?? ""}
                                onChange={(e) => alterarCampoItem("caixasUnitariasDevolvidas", e.target.value)}
                            />
                        </div>
                    )}
                </Modal>
            </main>
        </div>
    );
}
